import moment from "moment";
import { findIana } from "windows-iana";

class DateUtil {
  static getFormattedDownloadDate(): string {
    const currentDateTime = new Date();
    const day = currentDateTime.getDate().toString().padStart(2, "0");
    const month = (currentDateTime.getMonth() + 1).toString().padStart(2, "0");
    const year = currentDateTime.getFullYear().toString();
    const hour = currentDateTime.getHours().toString().padStart(2, "0");
    const minute = currentDateTime.getMinutes().toString().padStart(2, "0");
    const second = currentDateTime.getSeconds().toString().padStart(2, "0");
    const formattedDate = `${day}${month}${year}-${hour}${minute}${second}`;
    return formattedDate;
  }

  // Convert a YYYY-MM-DD calendar date to a UTC ISO instant using the
  // browser's local timezone (including DST for that date).
  // Start is local midnight; end is exclusive local midnight of the next day
  // so backend `< endDate` filters include the whole last local day.
  static setStartEndDate = (
    date: string | undefined,
    type: "start" | "end"
  ): string | undefined => {
    if (!date) return undefined;

    const parsed = moment(date.substring(0, 10), "YYYY-MM-DD", true);
    if (!parsed.isValid()) return undefined;

    const local =
      type === "start"
        ? parsed.startOf("day")
        : parsed.add(1, "day").startOf("day");

    return DateUtil.toUtcIsoString(local.toDate()) ?? undefined;
  };

  static getBrowserTimeZone = (): string =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  static setStartEndDatepicker = (
    date: string | undefined,
    type: "start" | "end"
  ): string | undefined => {
    if (!date) return undefined;
    let d = new Date(date);
    if (type === "start") {
      d.setHours(0, 0, 0, 0);
    } else {
      d.setHours(23, 59, 59, 999);
    }
    return this.convertToAtomDateTime(d);
  };

  static formatDate = (
    date: string | null | undefined,
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions | undefined
  ) => {
    if (locales === null || locales === undefined) {
      locales = "en-US";
    }
    if (options === null || options === undefined) {
      options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZoneName: "short",
      };
    }
    const formatter = new Intl.DateTimeFormat(locales, options);

    const dateObj = date ? new Date(date) : undefined;
    if (dateObj === undefined) return undefined;
    const formattedDate = formatter.format(dateObj);
    return formattedDate;
  };

  static convertUtcToLocalTime = (
    utcDateTime: string | null | undefined
  ): string => {
    if (!utcDateTime) return "";

    // Check if the input date-time string contains a "Z" at the end
    if (!utcDateTime.endsWith("Z")) {
      utcDateTime += "Z";
    }

    // Convert the UTC Date object to the local browser time
    const localDate = new Date(utcDateTime.toLocaleString());

    return localDate.toString();
  };

  static padZero = (value: number): string => {
    return value.toString().padStart(2, "0");
  };

  static convertToAtomDateTime = (localDateTime: Date): string => {
    const year = localDateTime.getFullYear();
    const month = DateUtil.padZero(localDateTime.getMonth() + 1);
    const day = DateUtil.padZero(localDateTime.getDate());
    const hours = DateUtil.padZero(localDateTime.getHours());
    const minutes = DateUtil.padZero(localDateTime.getMinutes());
    const seconds = DateUtil.padZero(localDateTime.getSeconds());

    // Get the local timezone offset
    const timezoneOffsetMinutes = localDateTime.getTimezoneOffset();
    const timezoneOffsetHours = -Math.floor(timezoneOffsetMinutes / 60);
    const timezoneOffsetMinutesRemainder = Math.abs(timezoneOffsetMinutes % 60);
    const timezoneOffsetSign = timezoneOffsetHours >= 0 ? "+" : "-";
    const timezoneOffset = `${timezoneOffsetSign}${DateUtil.padZero(
      timezoneOffsetHours
    )}:${DateUtil.padZero(timezoneOffsetMinutesRemainder)}`;

    // Format the local datetime to the desired string format
    const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezoneOffset}`;

    return formattedDateTime;
  };

  // return local date in "dd MMM yyyy, hh:mm:ss AM/PM" format
  static getLocalDateFormat = (
    utcDateTime: Date | null | undefined
  ): string => {
    if (!utcDateTime) return "";

    const formattedDate = utcDateTime.toLocaleString("en-AU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
    return formattedDate;
  };

  // Format as "dd MMM yyyy, hh:mm:ss AM/PM"
  static getLocalDateFormatFromString = (
    utcDateTimeString: string | null | undefined
  ): string => {
    if (!utcDateTimeString) return "";
    const date = new Date(utcDateTimeString);
    return DateUtil.getLocalDateFormat(date);
  };

  // Format as "dd MMM yyyy"
  static getLocalDateOnlyFormatFromString = (
    utcDateTimeString: string | null | undefined
  ): string => {
    return DateUtil.getLocalDateFormatFromString(utcDateTimeString).split(
      ","
    )[0];
  };

  static formatTimeToAmPm(time: string): string {
    if (!time) return "";

    // If time is already in proper format (HH:MM:SS or HH:MM)
    const timeParts = time.split(":");
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12; // Convert to 12-hour format
      return `${formattedHours}:${minutes} ${ampm}`;
    }

    return time; // Return original if format not recognized
  }

  static formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const isSameDay = start === end;
    const isSameMonth =
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();
    const isSameYear = startDate.getFullYear() === endDate.getFullYear();

    const day = (d: Date) => String(d.getDate()).padStart(2, "0");
    const month = (d: Date) => d.toLocaleString("default", { month: "short" });
    const year = (d: Date) => d.getFullYear();

    if (isSameDay) {
      return `${day(startDate)} ${month(startDate)} ${year(startDate)}`;
    }

    if (isSameMonth) {
      return `${day(startDate)}–${day(endDate)} ${month(startDate)} ${year(
        startDate
      )}`;
    }

    if (isSameYear) {
      return `${day(startDate)} ${month(startDate)}–${day(endDate)} ${month(
        endDate
      )} ${year(startDate)}`;
    }

    return `${day(startDate)} ${month(startDate)} ${year(startDate)}–${day(
      endDate
    )} ${month(endDate)} ${year(endDate)}`;
  };

  static formatTimeRange = (start: string, end: string) => {
    return `${DateUtil.formatTimeToAmPm(start)} – ${DateUtil.formatTimeToAmPm(
      end
    )}`;
  };

  // Returns date in YYYY-MM-DD format
  static getDatePartOnly = (date: Date | null | undefined): string => {
    if (!date) return "";
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  // Returns date in 01 May 2025 format
  static getFormattedDateFromUtcToLocalTimezone = (
    dateString: string
  ): string => {
    if (!dateString) return "";
    // Remove "Z" if present to avoid timezone issues as this is a local time but in UTC format
    const date = new Date(dateString.replace("Z", ""));

    // Get date components
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    // Format as "01 May 2025"
    return `${day} ${month} ${year}`;
  };

  // Returns date in "Wed, 01 May, 2025" format
  static getFormattedDateWithWeekday = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString.replace("Z", ""));

    const weekday = date.toLocaleString("en-US", { weekday: "short" });
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    // Format as "Wed, 01 May, 2025"
    return `${weekday}, ${day} ${month}, ${year}`;
  };

  // Returns time in 09:00AM format
  static getFormattedTimeFromUtcToLocalTimezone = (
    dateString: string
  ): string => {
    if (!dateString) return "";
    // Remove "Z" if present to avoid timezone issues as this is a local time but in UTC format
    const date = new Date(dateString.replace("Z", ""));

    // Get time components
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
    // Format as "09:00AM"
    return `${formattedHours}:${minutes}${ampm}`;
  };

  // Returns date/time in "7 Oct 2026, 2:30 PM" format
  static getFormattedScheduleDateTime = (
    date: Date | null | undefined
  ): string => {
    if (!date || isNaN(date.getTime())) return "";

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;

    return `${day} ${month} ${year}, ${hour12}:${minutes} ${ampm}`;
  };

  // Returns date/time in "Wed, 7 Oct 2026, 10:00AM" format
  static getFormattedNotificationSummaryDateTime = (
    dateString: string | null | undefined
  ): string => {
    if (!dateString) return "";
    const date = new Date(dateString.replace("Z", ""));
    if (isNaN(date.getTime())) return "";

    const weekday = date.toLocaleString("en-US", { weekday: "short" });
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const time = DateUtil.getFormattedTimeFromUtcToLocalTimezone(dateString);

    return `${weekday}, ${day} ${month} ${year}, ${time}`;
  };

  static getLocalDateTimeFromUtcDateTimeString = (
    utcDateTimeString: string | null | undefined
  ): Date | null => {
    if (!utcDateTimeString) return null;

    // Remove "Z" if present to avoid timezone issues as this is a local time but in UTC format
    const date = new Date(utcDateTimeString.replace("Z", ""));
    if (isNaN(date.getTime())) return null; // Check for invalid date

    return date;
  };

  static getLocalDateTimeInUTCFormatFromDateObject = (
    date: Date | null | undefined
  ): string | null => {
    if (!date) return null;

    // extract the date components to avoid timezone issues and return a UTC date string in ISO format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.0000000Z`;
  };

  // Convert a Date to an actual UTC ISO string using the .NET-style 7-digit
  // fractional second suffix the backend expects.
  static toUtcIsoString = (date: Date | null | undefined): string | null =>
    date ? date.toISOString().replace(/\.[0-9]{3}Z$/, ".0000000Z") : null;

  // Convert a UTC datetime string into a Date whose *local* component getters
  // (getFullYear/getHours/...) report the wall-clock time in the given Windows
  // timezone id (e.g. "AUS Eastern Standard Time"). Used to hydrate form
  // values from UTC strings while keeping the datetime-local input correct.
  static utcStringToLocalDateInTimezone = (
    utcDateTimeString: string | null | undefined,
    windowsTimezoneId: string | null | undefined
  ): Date | null => {
    if (!utcDateTimeString || !windowsTimezoneId) return null;

    const utcDate = new Date(utcDateTimeString);
    if (isNaN(utcDate.getTime())) return null;

    const ianaList = findIana(windowsTimezoneId);
    const ianaName = ianaList?.[0];
    if (!ianaName) return null;

    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: ianaName,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(utcDate);

    const lookup: Record<string, string> = {};
    for (const part of parts) {
      if (part.type !== "literal") lookup[part.type] = part.value;
    }

    // Some engines emit "24" for midnight when hour12 is false.
    const hour = lookup.hour === "24" ? 0 : parseInt(lookup.hour, 10);

    return new Date(
      parseInt(lookup.year, 10),
      parseInt(lookup.month, 10) - 1,
      parseInt(lookup.day, 10),
      hour,
      parseInt(lookup.minute, 10),
      parseInt(lookup.second, 10)
    );
  };

  // Convert Date object to timezone-agnostic ISO string (YYYY-MM-DDTHH:mm:ss)
  // Extracts local date/time components without applying timezone conversion
  static dateToTimezoneAgnosticISO = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`;
  };

  // Parse timezone-agnostic ISO string to Date object (without timezone conversion)
  // Handles formats: YYYY-MM-DDTHH:mm:ss or YYYY-MM-DDTHH:mm:ss.SSSSSSSZ
  // Returns null if string is invalid
  static timezoneAgnosticISOToDate = (
    isoString: string | null | undefined
  ): Date | null => {
    if (!isoString) return null;

    // Strip milliseconds and Z suffix: YYYY-MM-DDTHH:mm:ss.SSSSSSSZ -> YYYY-MM-DDTHH:mm:ss
    const cleaned = isoString.replace(/\.\d+Z?$/, "");
    const match = cleaned.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
    );

    if (!match) return null;

    const [, year, month, day, hours, minutes, seconds] = match;
    // Use Date constructor with components to avoid timezone conversion
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds)
    );
  };

  // Return timezone-agnostic date string in ISO format (YYYY-MM-DD)
  // Strips any time or timezone information and returns just the date part
  static getTimezoneAgnosticDateFormat = (
    dateString: string | null | undefined
  ): string => {
    if (!dateString) return "";

    // Parse without timezone conversion: YYYY-MM-DD
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return "";

    // Return ISO date format: YYYY-MM-DD
    return match[0];
  };

  // Return timezone-agnostic datetime string in ISO format (YYYY-MM-DDTHH:mm:ss)
  // Strips any milliseconds, timezone suffix, and returns clean ISO format
  static getTimezoneAgnosticDateTimeFormat = (
    dateTimeString: string | null | undefined
  ): string => {
    if (!dateTimeString) return "";

    // Parse without timezone conversion: YYYY-MM-DDTHH:mm:ss
    // Also handle format with milliseconds and Z: YYYY-MM-DDTHH:mm:ss.SSSSSSSZ
    const cleaned = dateTimeString.replace(/\.\d+Z?$/, "");
    const match = cleaned.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
    );
    if (!match) return "";

    // Return ISO datetime format: YYYY-MM-DDTHH:mm:ss
    return match[0];
  };

  // Return a Date whose local getters report wall-clock time in the given
  // Windows timezone id (e.g. "AUS Eastern Standard Time"), including DST.
  // Prefer this over parsing the fixed "(UTC±HH:MM)" prefix from display labels.
  static getGivenDateTimeInTimezone = (
    windowsTimezoneId: string,
    date?: Date
  ): Date => {
    const inputDate = date || new Date();
    const result = this.utcStringToLocalDateInTimezone(
      inputDate.toISOString(),
      windowsTimezoneId
    );

    if (!result) {
      throw new Error(`Invalid timezone: ${windowsTimezoneId}`);
    }

    return result;
  };

  static validateTimeDifference(
    currentTimeStr: string | null,
    selectedTimeStr: string | null,
    hours: number
  ): boolean {
    const current = new Date(currentTimeStr ?? "");
    const selected = new Date(selectedTimeStr ?? "");

    if (isNaN(current.getTime()) || isNaN(selected.getTime())) {
      throw new Error("Invalid date format provided");
    }

    const differenceInMs = selected.getTime() - current.getTime();
    const requiredTimeInMs = hours * 3600000; // hours * (60 * 60 * 1000)

    return differenceInMs >= requiredTimeInMs;
  }

  // Friendly label for Windows timezone ids shown in notification preview/list.
  // Use a year-round name for zones that observe DST so the UI does not switch
  // between e.g. AEST and AEDT depending on the scheduled date.
  static getTimezoneDisplayName = (
    windowsTimezoneId: string | null | undefined
  ): string => {
    if (!windowsTimezoneId) return "";

    const displayNames: Record<string, string> = {
      "AUS Eastern Standard Time": "Australian Eastern Time",
    };

    return displayNames[windowsTimezoneId] ?? windowsTimezoneId;
  };
}

export default DateUtil;
